import { useFieldArray, useFormContext } from 'react-hook-form';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import RecordView from 'components/RecordView';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import { useLayoutContext } from 'contexts/LayoutContext';
import { InferredFeeSettingsFormType } from './FormWrapper';
import { createEmptyFeeTemplate } from './helpers';
import FeeTemplateSection from './FeeTemplateSection';
import { useState } from 'react';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { useDisclosure } from 'further-ui/hooks';

const FeeSettingsForm: React.FC = () => {
  const mutationOpts = useHandleTrpcMutation();
  const { selectedVehicleId } = useLayoutContext();
  const updateWarningModal = useDisclosure();

  const [hasEditedTemplateWithCommitment, setHasEditedTemplateWithCommitment] =
    useState(false);

  const updateVehicleFeeSettings = api.vehicle.updateFeeSettings.useMutation(
    mutationOpts({
      successMessage: 'Vehicle updated successfully.',
      onSuccess: () => {
        setHasEditedTemplateWithCommitment(false);
      },
    }),
  );

  const {
    control,
    getValues,
    reset,
    handleSubmit,
    formState: { dirtyFields },
  } = useFormContext<InferredFeeSettingsFormType>();

  const { fields } = useFieldArray({
    control,
    name: 'lpFeeTemplates',
  });

  const addNewFeeTemplate = () => {
    const { lpFeeTemplates, ...rest } = getValues();
    reset({
      ...rest,
      lpFeeTemplates: [
        ...lpFeeTemplates,
        {
          ...createEmptyFeeTemplate(lpFeeTemplates.length === 0),
        },
      ],
    });
  };

  const removeTemplate = (index: number) => {
    const { lpFeeTemplates, ...rest } = getValues();
    const afterRemoval = lpFeeTemplates.filter((_, i) => i !== index);
    const hasDefault = afterRemoval.some((t) => t.isDefaultFeeTemplate);

    const newLpFeeTemplates =
      afterRemoval.length > 0 && !hasDefault
        ? [
            { ...afterRemoval[0], isDefaultFeeTemplate: true },
            ...afterRemoval.slice(1),
          ]
        : afterRemoval;

    reset({
      ...rest,
      lpFeeTemplates: newLpFeeTemplates,
    });
  };

  const hasSignificantChanges = (index: number) => {
    const dirtyTemplateFields = dirtyFields.lpFeeTemplates?.[index];
    if (!dirtyTemplateFields) return false;

    // Check if any fields other than name or isDefaultFeeTemplate are dirty
    const { name, isDefaultFeeTemplate, lpFeeTemplateFees, ...otherFields } =
      dirtyTemplateFields;

    const feeTemplateHasChanges = Object.values(otherFields).some(
      (value) => value,
    );
    const feeTemplateFeesHaveChanges = lpFeeTemplateFees?.some((fee) =>
      Object.values(fee).some((value) => value),
    );
    return feeTemplateHasChanges || feeTemplateFeesHaveChanges;
  };

  const onSubmit = async (data: InferredFeeSettingsFormType) => {
    let hasChanges = false;

    for (let i = 0; i < data.lpFeeTemplates.length; i++) {
      const template = data.lpFeeTemplates[i];
      if (!template.hasCommitments && !template.hasApprovedFees) {
        continue;
      }
      if (template._id && hasSignificantChanges(i)) {
        hasChanges = true;
        break;
      }
    }

    if (hasChanges || hasEditedTemplateWithCommitment) {
      updateWarningModal.onOpen();
    } else {
      updateVehicleFeeSettings.mutate({
        ...data,
        vehicleId: selectedVehicleId ?? '',
      });
    }
  };

  const handleConfirmSubmit = () => {
    updateVehicleFeeSettings.mutate({
      ...getValues(),
      vehicleId: selectedVehicleId ?? '',
    });
    updateWarningModal.onClose();
  };

  return (
    <form>
      <RecordView size="full">
        {fields.map((field, index) => (
          <FeeTemplateSection
            key={field.id}
            index={index}
            remove={removeTemplate}
            hasApprovedFees={!!field.hasApprovedFees}
            hasCommitments={!!field.hasCommitments}
            setHasEditedTemplateWithCommitment={
              setHasEditedTemplateWithCommitment
            }
          />
        ))}
        <ButtonGroup sx={{ justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            type="button"
            onClick={addNewFeeTemplate}
            size="large"
          >
            Add fee template
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="button"
            onClick={handleSubmit(onSubmit)}
            loading={updateVehicleFeeSettings.isPending}
          >
            Save
          </Button>
        </ButtonGroup>
      </RecordView>

      <ConfirmDialog
        open={updateWarningModal.isOpen}
        onClose={updateWarningModal.onClose}
        title="Warning"
        content="You are about to make changes to fee templates that have existing commitments. This will cause any unapproved fees to be recalculated. Are you sure you want to proceed?"
        actions={{
          confirm: {
            label: 'Proceed',
            onClick: handleConfirmSubmit,
          },
        }}
      />
    </form>
  );
};

export default FeeSettingsForm;
